import React from 'react';

import {formatNumber, useI18n} from '@udemy/i18n';
import {TextSkeleton} from '@udemy/react-reveal-components';

export const useEnrollmentDisplay = () => {
    const {gettext, interpolate} = useI18n();

    const roundToNearestHundredth = (num: number) => {
        return Math.floor(num / 100) * 100;
    };

    const abbreviateEnrollment = (num_enrollments: number, showCompact?: boolean) => {
        if (showCompact) {
            return interpolate(
                `%(num_enrollments)s+`,
                {
                    num_enrollments: num_enrollments.toLocaleString(undefined, {
                        notation: 'compact',
                        compactDisplay: 'short',
                        roundingMode: 'floor',
                    }),
                },
                true,
            );
        }

        if (num_enrollments >= 1000000) {
            const numString = num_enrollments.toString();
            const numLength = numString.length;
            const digits = numLength % 3 || 3;
            const firstPart = numString.substring(0, digits);
            const remainder = numString.substring(digits, 1);
            const abbreviation = `${firstPart}.${remainder}`;

            return interpolate(
                gettext('%(num_enrollments)sM+ learners'),
                {
                    num_enrollments: formatNumber(abbreviation),
                },
                true,
            );
        }

        if (num_enrollments >= 100000) {
            const numString = num_enrollments.toString();
            const abbreviation = numString.substring(0, 3);

            return interpolate(
                gettext('%(num_enrollments)sk+ learners'),
                {
                    num_enrollments: formatNumber(abbreviation),
                },
                true,
            );
        }

        return interpolate(
            gettext('%(num_enrollments)s+ learners'),
            {
                num_enrollments: formatNumber(roundToNearestHundredth(num_enrollments)),
            },
            true,
        );
    };

    const renderEnrollments = (
        num_enrollments: number | undefined,
        skeletonClassName?: string,
        showCompact?: boolean,
    ) => {
        if (num_enrollments) {
            return (
                <div data-testid="num-enrollments">
                    {abbreviateEnrollment(num_enrollments, showCompact)}
                </div>
            );
        }

        if (num_enrollments === 0) {
            return null;
        }

        return (
            <TextSkeleton
                className={skeletonClassName}
                lineCountPerParagraph={1}
                paragraphCount={1}
            />
        );
    };

    return {
        roundToNearestHundredth,
        abbreviateEnrollment,
        renderEnrollments,
    };
};
